import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { context, propagation } from '@opentelemetry/api'
import { logger } from '@/logger'
import { redactForLog } from '@/logger/redact'
import {
  BORA_TENANT_HEADER,
  FORWARDED_HOST_HEADER,
  HOST_HEADER,
  getConfiguredTenant,
  getRequestTenantKey,
} from '@/configs/tenant/tenantRouting'

/**
 * Axios instances for API communication
 *
 * Note: proxy: false is set on all instances to:
 * 1. Avoid url.parse() deprecation warnings from proxy-from-env package
 * 2. Skip unnecessary proxy detection (not needed for internal API calls)
 *
 * If proxy support is needed in the future, configure proxy explicitly.
 * See: https://axios-http.com/docs/req_config
 */

const appInstance: AxiosInstance = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL,
  proxy: false, // Disable proxy detection
})

const cmsInstance: AxiosInstance = axios.create({
  baseURL: process.env.CMS_API_BASE,
  proxy: false, // Disable proxy detection
})

/**
 * Local debugging only: set DEBUG_API_REQUEST=true in your own environment (never in the committed
 * .env, which goes into the image) to log every outgoing call. Headers and body go through
 * redactForLog, so the Basic credentials, the customer token and the guest's data stay out.
 */
function isApiRequestDebugEnabled(): boolean {
  return process.env.DEBUG_API_REQUEST === 'true' && typeof window === 'undefined'
}

cmsInstance.interceptors.request.use(function (config) {
  if (isApiRequestDebugEnabled()) {
    logger.debug(
      `this => CMS: ${config.url}`,
      redactForLog({
        method: config.method?.toUpperCase(),
        url: `${config.baseURL}${config.url}`,
        headers: config.headers,
        data: config.data,
      })
    )
  }

  return config
})

/**
 * Basic credentials as a header, never through axios' `auth` option.
 *
 * `auth` becomes the `auth` field of the Node http request options, and the OpenTelemetry
 * http instrumentation builds its span name and `http.url` out of those options. That wrote
 * `http://frontend:<password>@f-edge.shared/api/v1/config` into Cloud Trace, in clear, for
 * anyone with trace access to read (measured on bora-test, 2026-09-28). A header is not part
 * of the URL, so it is not recorded.
 *
 * Returns undefined when either half is missing: the browser bundle has neither (both are
 * server-only variables), and `auth` used to be set there too, as `undefined:undefined`.
 */
function basicAuthHeader(username?: string, password?: string): string | undefined {
  if (!username || !password) {
    return undefined
  }

  const credentials = `${username}:${password}`
  const encoded =
    typeof Buffer !== 'undefined' ? Buffer.from(credentials, 'utf8').toString('base64') : btoa(credentials)

  return `Basic ${encoded}`
}

const apiAuthHeader = basicAuthHeader(process.env.API_USERNAME, process.env.API_PASSWORD)

const configuredTenant = getConfiguredTenant()

const apiInstance: AxiosInstance = axios.create({
  baseURL: process.env.API_BASE,
  headers: {
    // Only a one-client deployment names its tenant here. An empty TENANT must not go out as an empty header.
    ...(configuredTenant ? { [BORA_TENANT_HEADER]: configuredTenant } : {}),
    ...(apiAuthHeader ? { Authorization: apiAuthHeader } : {}),
  },
  proxy: false, // Disable proxy detection
})

type TimedInternalAxiosRequestConfig = InternalAxiosRequestConfig & {
  timingMetadata?: {
    requestStartedAt: number
  }
}

function isApiTimingEnabled(): boolean {
  return process.env.DEBUG_API_TIMING === 'true' && typeof window === 'undefined'
}

function getRequestUrl(config: InternalAxiosRequestConfig): string | undefined {
  if (!config.url) {
    return undefined
  }

  if (config.baseURL) {
    return `${config.baseURL}${config.url}`
  }

  return config.url
}

function getDurationSeconds(config: TimedInternalAxiosRequestConfig | undefined): number | undefined {
  const requestStartedAt = config?.timingMetadata?.requestStartedAt

  if (requestStartedAt === undefined) {
    return undefined
  }

  return Number(((performance.now() - requestStartedAt) / 1000).toFixed(3))
}

// Carry this request's trace into f-edge, so one booking is one trace rather than one per call.
// The registered propagators do this for fetch, but not for axios, which goes through Node's http
// module. Outside a traced request there is nothing to inject and the headers are left alone.
export function injectTraceContext(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  propagation.inject(context.active(), config.headers, {
    set: (headers, key, value) => headers.set(key, value),
  })

  return config
}

/**
 * Multi-tenant (trial) mode, no TENANT: every f-edge call names the tenant by the public host of the incoming
 * request, in the standard `Host` header (server side only: a browser cannot set `Host`). A call that already
 * carries the header keeps it: a function cached with `unstable_cache` cannot read the request headers, so it gets
 * the host as an argument and sets it itself. No `X-Forwarded-Host` goes out, because f-edge would take it over `Host`.
 * With TENANT set this does nothing, and the instance sends `X-Bora-Tenant` as before.
 */
export async function addRequestHostHeader(config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> {
  if (typeof window !== 'undefined' || getConfiguredTenant()) {
    return config
  }

  // In this mode no X-Bora-Tenant and no X-Forwarded-Host go out, whatever a caller puts into its headers
  config.headers.delete(BORA_TENANT_HEADER)
  config.headers.delete(FORWARDED_HOST_HEADER)

  if (!config.headers.has(HOST_HEADER)) {
    config.headers.set(HOST_HEADER, await getRequestTenantKey())
  }

  return config
}

apiInstance.interceptors.request.use(addRequestHostHeader)

apiInstance.interceptors.request.use(injectTraceContext)

apiInstance.interceptors.request.use(function (config) {
  if (isApiTimingEnabled()) {
    ;(config as TimedInternalAxiosRequestConfig).timingMetadata = {
      requestStartedAt: performance.now(),
    }
  }

  if (isApiRequestDebugEnabled()) {
    logger.debug(
      `SALES => F-EDGE: ${config.url}`,
      redactForLog({
        TENANT: process.env.TENANT,
        method: config.method?.toUpperCase(),
        url: `${config.baseURL}${config.url}`,
        headers: config.headers,
        data: config.data,
      })
    )
  }

  return config
})

apiInstance.interceptors.response.use(
  function (response) {
    if (isApiTimingEnabled()) {
      logger.debug(`F-EDGE => SALES: ${getRequestUrl(response.config)}`, {
        durationSeconds: getDurationSeconds(response.config as TimedInternalAxiosRequestConfig),
        method: response.config.method?.toUpperCase(),
        status: response.status,
        tenant: process.env.TENANT,
      })
    }

    return response
  },
  function (error: AxiosError) {
    if (isApiTimingEnabled()) {
      logger.error('F-EDGE API request failed', {
        durationSeconds: getDurationSeconds(error.config as TimedInternalAxiosRequestConfig | undefined),
        method: error.config?.method?.toUpperCase(),
        status: error.response?.status,
        tenant: process.env.TENANT,
        url: error.config ? getRequestUrl(error.config) : undefined,
      })
    }

    return Promise.reject(error)
  }
)

export { appInstance, cmsInstance, apiInstance }
